import { Data } from 'signup-constants/signupData/Data';
import { companyInfo } from '../http-company-info/CompanyInfoClient';
import { handleCompanyInfoError, handleCompanyInfoSuccess } from '../http-company-info/handleCompanyInfoResponse';
import { getIsSuccessfullyVerified, getVerificationType } from '../store/app/selectors';
import { isStepInNextOrCurrentStep } from '../store/flow/selectors';
import { VisitorContextActionTypes } from '../store/visitor-context/action-types';
import { Step } from 'signup-constants/Step';
import { fetchPublicVisitorContext } from '../visitor-context-fetch/fetchPublicVisitorContext';
import { handleVisitorContextSuccess } from '../visitor-context-fetch/handleVisitorContextResponse';
import { EndpointKey } from './EndpointKey';
import { FlowActionTypes } from '../store/flow/action-types';
import { hasEndpointBeenFetched, isEndpointStatusFetching } from '../store/http-responses/selectors';
import { getIsActiveAndAssignedToVariant } from '../store/experiments/selectors';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { GTKMWithDomainFromEmail, getGTKMCompanyInfoClient } from '../http-gtkm-company-info/GTKMCompanyInfoClient';
import { getCurrentStep } from '../store/flow/selectors';
import { shouldOnlyFetchGTKMData } from '../http-gtkm-company-info/fetchOnlyGTKMDataChecks';
import { isExistingUser, isExistingUserExistingAccount } from '../app/verificationTypeSelectors';
import { handleACQ177GTKMCompanyInfoError, handleACQ177GTKMCompanyInfoSuccess } from '../http-gtkm-company-info/handleACQ177GTKMCompanyInfoResponse';
import { getGTKMEndpointKey } from '../utils/gtkm/getGTKMEndpointKey';
import { isAssignedToGTKMExperiment } from '../utils/gtkm/isAssignedToGTKM';
import { isEligibleForACQ177ProductisedFlow } from '../utils/gtkm/isEligibleForACQ177ProductisedFlow';
import { isPurchaseProductisedFlow } from '../utils/gtkm/isPurchaseProductisedFlow';
import { getIsExistingUserSession } from '../store/app/selectors';
import { isMarketingProductisedFlow } from '../utils/gtkm/isMarketingProductisedFlow';
import { handleSU0004GTKMCompanyInfoError, handleSU0004GTKMCompanyInfoSuccess } from '../http-gtkm-company-info/handleSU0004GTKMCompanyInfoResponse';
import { isProductisedSignupFlow } from '../utils/gtkm/isProductisedSignupFlow';

// NOTE: This object was wrapped in a function to avoid problematic cycles when frontend-infra-core migrates to Webpack 5
export const getEndpointsPrefilling = () => ({
  [EndpointKey.CompanyInfo]: {
    getRequestData: () => [Data.Email],
    handleError: handleCompanyInfoError,
    handleSuccess: handleCompanyInfoSuccess,
    method: companyInfo,
    stateCheck: state => {
      const isAssignedToGTKM = isAssignedToGTKMExperiment(state);

      // Don't invoke the original endpoint for ACQ173 or ACQ177
      if (isAssignedToGTKM) {
        return false;
      }
      const isVerificationTypeSet = Boolean(getVerificationType(state));
      return isVerificationTypeSet && isStepInNextOrCurrentStep(state, [Step.CompanyDomain, Step.CompanyName, Step.Industry]);
    }
  },
  [EndpointKey.VisitorContextFetch]: {
    handleSuccess: handleVisitorContextSuccess,
    method: fetchPublicVisitorContext,
    reduxActionType: VisitorContextActionTypes.Fetch
  },
  // GTKMWithEmailDomain is required for agentic flow when the endpoint above is not invoked
  [EndpointKey.GTKMWithDomainFromEmail]: {
    getRequestData: () => [Data.Email],
    reduxActionType: FlowActionTypes.GoNextStep,
    once: true,
    stateCheck: state => {
      const isSuccessfullyVerifiedOrExisting = getIsSuccessfullyVerified(state) || isExistingUser(state) || isExistingUserExistingAccount(state);
      return isSuccessfullyVerifiedOrExisting && shouldOnlyFetchGTKMData(state, EndpointKey.GTKMWithDomainFromEmail);
    },
    method: GTKMWithDomainFromEmail
  },
  [EndpointKey.GTKMCompanyInfoACQ177]: {
    getRequestData: () => [Data.Email],
    handleError: handleACQ177GTKMCompanyInfoError,
    handleSuccess: handleACQ177GTKMCompanyInfoSuccess,
    method: getGTKMCompanyInfoClient,
    reduxActionType: FlowActionTypes.GoNextStep,
    stateCheck: state => {
      if (getGTKMEndpointKey(state) !== EndpointKey.GTKMCompanyInfoACQ177) {
        return false;
      }
      const endpointHasBeenFetched = hasEndpointBeenFetched(state, EndpointKey.GTKMCompanyInfoACQ177);
      if (endpointHasBeenFetched) {
        return false;
      }
      const isACQ0177Active = isEligibleForACQ177ProductisedFlow(state) || getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0177, [ExperimentParameterGroup.Variant_A]);
      if (!isACQ0177Active) {
        return false;
      }
      return getIsSuccessfullyVerified(state) || isExistingUser(state) && isPurchaseProductisedFlow() && getCurrentStep(state) === Step.LoadingPage || getIsExistingUserSession(state) && isMarketingProductisedFlow() && getCurrentStep(state) === Step.LoadingPage;
    }
  },
  [EndpointKey.GTKMCompanyInfoSU0004]: {
    getRequestData: () => [Data.Email],
    handleError: handleSU0004GTKMCompanyInfoError,
    handleSuccess: handleSU0004GTKMCompanyInfoSuccess,
    method: getGTKMCompanyInfoClient,
    reduxActionType: FlowActionTypes.GoNextStep,
    stateCheck: state => {
      if (getGTKMEndpointKey(state) !== EndpointKey.GTKMCompanyInfoSU0004) {
        return false;
      }
      const endpointHasBeenFetched = hasEndpointBeenFetched(state, EndpointKey.GTKMCompanyInfoSU0004);
      if (endpointHasBeenFetched) {
        return false;
      }
      if (isEndpointStatusFetching(EndpointKey.GTKMCompanyInfoSU0004)(state)) {
        return false;
      }
      if (!isProductisedSignupFlow(state)) {
        return false;
      }
      return getIsSuccessfullyVerified(state);
    }
  }
});